<template>
  <div class="admin-root">
    <div class="admin-wrapper" :class="{ mini: collapsed }">
      <!-- 左侧菜单（原版 common/sidebar 结构） -->
      <div class="admin-sidebar">
        <div class="nav-header">
          <span class="profile-img">
            <img v-if="avatar" :src="avatar" alt="" />
            <i v-else class="fa fa-user"></i>
          </span>
          <span class="profile-name">{{ displayName }}</span>
          <span class="profile-role">管理员</span>
        </div>
        <div class="logo-element">IN+</div>

        <ul class="side-menu">
          <li v-for="item in menu" :key="item.key">
            <!-- 一级无子菜单 -->
            <a v-if="!item.children" href="javascript:void(0)"
               :class="{ active: isActive(item.path) }" @click="go(item)">
              <i class="fa" :class="item.icon"></i><span>{{ item.title }}</span>
            </a>
            <!-- 一级含子菜单（metismenu 手风琴） -->
            <template v-else>
              <a href="javascript:void(0)"
                 :class="{ open: openKey === item.key, active: groupActive(item) }"
                 @click="toggle(item.key)">
                <i class="fa" :class="item.icon"></i><span>{{ item.title }}</span>
                <i class="fa arrow" :class="item.children[0].icon && 'fa-angle-left'"></i>
              </a>
              <ul class="nav-second-level" :class="{ open: openKey === item.key }">
                <li v-for="child in item.children" :key="child.key">
                  <a href="javascript:void(0)" :class="{ active: isActive(child.path) }" @click="go(child)">
                    <span>{{ child.title }}</span>
                  </a>
                </li>
              </ul>
            </template>
          </li>
        </ul>
      </div>

      <!-- 右侧主体（原版 page-wrapper 结构） -->
      <div class="admin-page">
        <!-- 顶栏（原版 common/header） -->
        <div class="admin-navbar">
          <button class="minimalize" @click="collapsed = !collapsed"><i class="fa fa-bars"></i></button>
          <input class="top-search" placeholder="请输入搜索内容" readonly />
          <div class="top-right">
            <span class="welcome-message">天星堪舆管理后台</span>
            <a class="logout-link" @click="onLogout"><i class="fa fa-sign-out"></i>注销</a>
          </div>
        </div>

        <!-- 页头标题条（原版 common/contop） -->
        <div class="dashboard-header">
          <h2>{{ pageTitle }}</h2>
        </div>

        <!-- 内容区 -->
        <div class="wrapper-content">
          <router-view />
        </div>

        <!-- 页脚（原版 common/footer） -->
        <div class="admin-footer">
          <div class="pull-right"><strong>https://www.txky-zg.com</strong></div>
          <div><strong>Copyright</strong> 天星堪舆&copy; 2023-2025</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { authApi } from '../api/admin'
import { getProfile } from '../api/request'

// 菜单结构：主页 / 权限 / 业务管理 与原版一致；
// 「扩展功能」为单独一级菜单，收纳原版没有的模块，便于后续调整。
const MENU = [
  { key: 'home', title: '主页', icon: 'fa-home', path: '/admin/dashboard' },
  {
    key: 'auth',
    title: '权限',
    icon: 'fa-key',
    children: [
      { key: 'administrators', title: '管理员', path: '/admin/administrators' },
      { key: 'roles', title: '角色', path: '/admin/roles' },
      { key: 'menus', title: '菜单', path: '/admin/menus' },
      { key: 'permissions', title: '权限', path: '/admin/permissions' },
      { key: 'policies', title: '策略', path: '/admin/policies' }
    ]
  },
  {
    key: 'base',
    title: '业务管理',
    icon: 'fa-users',
    children: [
      { key: 'users', title: '用户', path: '/admin/users' },
      { key: 'userBlacklist', title: '用户黑名单', path: '/admin/user-blacklist' },
      { key: 'sirens', title: '私人定制', path: '/admin/sirens' },
      { key: 'jizhais', title: '吉宅', path: '/admin/jizhais' },
      { key: 'prices', title: '价格', path: '/admin/prices' }
    ]
  },
  {
    key: 'cs',
    title: '客服管理',
    icon: 'fa-headphones',
    children: [
      // 客服工作台（/cs/*）的业务页在这里统一入口，页面组件与客服工作台完全共用一份，
      // 只是改成在后台内容区里内嵌展示（见 views/CustomerServiceHost.vue）。
      // 新增客服页面时：后台路由加一条（meta.csView 带上页面组件）+ 这里加一项。
      { key: 'csSelectionOrders', title: '自动择日提交信息', path: '/admin/cs-selection-orders' }
    ]
  },
  {
    key: 'extend',
    title: '扩展功能',
    icon: 'fa-cubes',
    children: [
      { key: 'autodates', title: '自动择日', path: '/admin/autodates' },
      // 老系统后台的「自动择日」是一个指向前台 /autoSelect 的 iframe；新系统沿用该工作流，
      // 但改为在**后台内容区**内嵌展示（/admin/course-config 里 iframe 载入
      // /tianxing/autoSelect），不再新开窗口，左侧菜单与后台现场都保留。
      { key: 'autoselect', title: '日课配置', path: '/admin/course-config' },
      { key: 'orders', title: '择日业务办理', path: '/admin/orders' },
      // 权限开通订单：星盘页「开通功能」的明细账，供按用户/权限/时间对账与导出
      { key: 'permissionOrders', title: '权限开通订单', path: '/admin/permission-orders' },
      { key: 'system', title: '系统设置', path: '/admin/system' }
    ]
  }
]

export default {
  name: 'AdminLayout',
  data () {
    return {
      collapsed: false,
      openKey: '',
      menu: MENU
    }
  },
  computed: {
    profile () {
      return getProfile()
    },
    displayName () {
      return this.profile ? (this.profile.display_name || this.profile.login_name) : '管理员'
    },
    avatar () {
      return this.profile && this.profile.avatar ? this.profile.avatar : ''
    },
    pageTitle () {
      const t = this.$route.meta && this.$route.meta.title
      return t ? `${t}管理` : '天星堪舆管理'
    }
  },
  watch: {
    // 进入页面时自动展开当前路由所在分组
    $route: {
      immediate: true,
      handler (to) {
        const group = MENU.find(m => m.children && m.children.some(c => c.path === to.path))
        if (group) this.openKey = group.key
      }
    }
  },
  methods: {
    isActive (path) {
      return this.$route.path === path
    },
    groupActive (item) {
      return item.children && item.children.some(c => c.path === this.$route.path)
    },
    toggle (key) {
      this.openKey = this.openKey === key ? '' : key
    },
    go (item) {
      // 全部菜单项都是后台内部路由：前台页面（如日课配置）也由对应后台页面
      // 在内容区里内嵌展示，因此统一走 router.push，不再有 window.open 分支。
      if (this.$route.path !== item.path) this.$router.push(item.path)
    },
    onLogout () {
      authApi.logout()
      this.$router.replace('/admin/login')
    }
  }
}
</script>

<style lang="scss">
@import url('/admin/font-awesome/css/font-awesome.min.css');
@import '../styles/admin-theme.scss';
</style>
